import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { directusAPI, fetchCurrentUser } from '../../api';
import TopBar from '../../layouts/TopBar/topBar';
import { parseISO } from 'date-fns';
import { formatDisplayDateTime } from '../../utils/dateFormatting';
import {
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  GlobeAltIcon,
  DevicePhoneMobileIcon
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(false);
  const [lastSaved, setLastSaved] = useState('');
  const [resolvedWorkspaceId, setResolvedWorkspaceId] = useState(localStorage.getItem('workspace_id'));

  const token = localStorage.getItem('directus_token');

  // Workspace ID resolution (consistent with other components)
  useEffect(() => {
    const getWorkspaceId = async () => {
      if (!resolvedWorkspaceId) {
        try {
          if (!token) return;

          const userData = await fetchCurrentUser(['linked_workspace.id']);
          if (userData?.linked_workspace?.id) {
            const workspaceId = userData.linked_workspace.id;
            localStorage.setItem('workspace_id', workspaceId);
            setResolvedWorkspaceId(workspaceId);
          }
        } catch (err) {
          console.error('Error fetching workspace ID:', err);
        }
      }
    };

    getWorkspaceId();
  }, [resolvedWorkspaceId, token]);

  // Fetch all chat sessions for this lead
  const fetchChatSessions = useCallback(async () => {
    if (!resolvedWorkspaceId || !lead) return;

    setSessionsLoading(true);
    try {
      // First, find all sessions linked to this lead by querying CRM records
      const crmResponse = await directusAPI.get('/items/crm', {
        params: {
          filter: {
            _and: [
              { linked_workspace: { _eq: resolvedWorkspaceId } },
              {
                _or: [
                  { lead_email: { _eq: lead?.lead_email } },
                  { lead_phone: { _eq: lead?.lead_phone } },
                  { id: { _eq: id } }
                ]
              }
            ]
          },
          fields: ['linked_session', 'date_created'],
          limit: -1
        }
      });

      const linkedSessions = crmResponse.data.data
        .filter(record => record.linked_session)
        .map(record => record.linked_session);

      if (linkedSessions.length > 0) {
        // Fetch session details for all linked sessions
        const sessionsResponse = await directusAPI.get('/items/chat_sessions', {
          params: {
            filter: {
              _and: [
                { id: { _in: linkedSessions } },
                { linked_workspace: { _nnull: true } },  // 🔒 ZAWSZE wykluczaj sesje bez workspace
                { linked_workspace: { _eq: resolvedWorkspaceId } }
              ]
            },
            fields: ['*', 'user_created.first_name', 'user_created.last_name', 'user_created.email'],
            sort: '-date_created',
            limit: -1
          }
        });

        // For each session, get the first message to use as preview
        const sessionsWithPreview = await Promise.all(
          sessionsResponse.data.data.map(async (session) => {
            try {
              const firstMessageResponse = await directusAPI.get('/items/chat_messages', {
                params: {
                  filter: {
                    session_id: { _eq: session.id },
                    role: 'user'
                  },
                  sort: 'order,date_created',
                  fields: ['content'],
                  limit: 1
                }
              });

              const firstMessage = firstMessageResponse.data.data?.[0]?.content || null;

              return {
                ...session,
                firstMessage: firstMessage,
                messageCount: await getMessageCount(session.id)
              };
            } catch (error) {
              console.error(`Error fetching first message for session ${session.id}:`, error);
              return {
                ...session,
                firstMessage: null,
                messageCount: 0
              };
            }
          })
        );

        setChatSessions(sessionsWithPreview);
      } else {
        setChatSessions([]);
      }
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
      setChatSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, [id, lead, resolvedWorkspaceId]);

  // Fetch lead details
  useEffect(() => {
    const fetchLead = async () => {
      if (!resolvedWorkspaceId) return;

      setLoading(true);
      try {
        const response = await directusAPI.get(`/items/crm/${id}`, {
          params: {
            fields: ['*'],
            filter: {
              linked_workspace: {
                _eq: resolvedWorkspaceId
              }
            }
          }
        });

        const leadData = response.data.data;
        setLead(leadData);
        setLastSaved(leadData.date_updated || leadData.date_created);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching lead:', error);
        setError('Failed to fetch lead details.');
        setLoading(false);
      }
    };

    if (resolvedWorkspaceId) {
      fetchLead();
    }
  }, [id, resolvedWorkspaceId, token]);

  useEffect(() => {
    fetchChatSessions();
  }, [fetchChatSessions]);

  // Helper function to get message count for a session
  const getMessageCount = async (sessionId) => {
    try {
      const response = await directusAPI.get('/items/chat_messages', {
        params: {
          filter: { session_id: { _eq: sessionId } },
          aggregate: { count: '*' }
        }
      });
      return response.data.data?.[0]?.count || 0;
    } catch (error) {
      return 0;
    }
  };

  // Update lead status
  const updateLeadStatus = useCallback(async (newStatus) => {
    setUpdating(true);
    try {
      const response = await directusAPI.patch(`/items/crm/${id}`, {
        status: newStatus,
        date_updated: new Date().toISOString()
      });

      setLead(prev => ({ ...prev, status: newStatus, date_updated: response.data.data.date_updated }));
      setLastSaved(response.data.data.date_updated);
    } catch (error) {
      console.error('Error updating lead status:', error);
    } finally {
      setUpdating(false);
    }
  }, [id]);

  const formatDateUpdated = (dateString) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      return formatDisplayDateTime(date, localStorage.getItem('i18nextLng') || 'en');
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      new: { color: 'bg-blue-100 text-blue-800', label: 'New' },
      contacted: { color: 'bg-yellow-100 text-yellow-800', label: 'Contacted' },
      qualified: { color: 'bg-green-100 text-green-800', label: 'Qualified' },
      converted: { color: 'bg-purple-100 text-purple-800', label: 'Converted' },
      lost: { color: 'bg-red-100 text-red-800', label: 'Lost' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const goBack = () => {
    navigate('/ai-agents/leads');
  };

  if (loading) {
    return (
      <div className="bg-transparent min-h-full">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="bg-transparent min-h-full">
        <div className="text-center py-12">
          <div className="text-red-600 text-lg">{error || 'Lead not found'}</div>
          <button
            onClick={goBack}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
          >
            Back to CRM
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-transparent min-h-full">
      <TopBar
        onGoBack={goBack}
        loading={updating}
        lastSaved={lastSaved}
        formatDateUpdated={formatDateUpdated}
        showSaveStatus={true}
        customMiddleContent={
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              {lead.lead_first_name} {lead.lead_last_name}
            </h1>
            {getStatusBadge(lead.status)}
          </div>
        }
      />

      <div className="px-4 sm:px-6 lg:px-10">
        <div className="max-w-[1500px] mx-auto py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Lead Information Card */}
            <div className="lg:col-span-1">
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <UserIcon className="h-5 w-5 mr-2 text-gray-400" />
                    Lead Information
                  </h3>
                </div>
                <div className="px-6 py-5 space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <p className="text-sm text-gray-900 font-medium">
                      {lead.lead_first_name} {lead.lead_last_name}
                    </p>
                  </div>

                  {lead.lead_email && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <div className="flex items-center space-x-2">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        <a href={`mailto:${lead.lead_email}`} className="text-sm text-primary-600 hover:text-primary-800">
                          {lead.lead_email}
                        </a>
                      </div>
                    </div>
                  )}

                  {lead.lead_phone && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <div className="flex items-center space-x-2">
                        <PhoneIcon className="h-4 w-4 text-gray-400" />
                        <a href={`tel:${lead.lead_phone}`} className="text-sm text-primary-600 hover:text-primary-800">
                          {lead.lead_phone}
                        </a>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="flex items-center justify-between">
                      {getStatusBadge(lead.status)}
                      <Menu as="div" className="relative inline-block text-left">
                        <Menu.Button className="form-dropdown-trigger inline-flex justify-center focus:ring-offset-2">
                          Update Status
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                            <div className="py-1">
                              {['new', 'contacted', 'qualified', 'converted', 'lost'].map((status) => (
                                <Menu.Item key={status}>
                                  {({ active }) => (
                                    <button
                                      onClick={() => updateLeadStatus(status)}
                                      className={classNames(
                                        active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                        lead.status === status ? 'bg-primary-50 text-primary-700' : '',
                                        'block px-4 py-2 text-sm w-full text-left'
                                      )}
                                      disabled={updating}
                                    >
                                      {status.charAt(0).toUpperCase() + status.slice(1)}
                                    </button>
                                  )}
                                </Menu.Item>
                              ))}
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-4 w-4 text-gray-400" />
                      <p className="text-sm text-gray-600">
                        {formatDateUpdated(lead.date_created)}
                      </p>
                    </div>
                  </div>

                  {lead.date_updated && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4 text-gray-400" />
                        <p className="text-sm text-gray-600">
                          {formatDateUpdated(lead.date_updated)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sessions</label>
                    <div className="flex items-center space-x-2">
                      <ChatBubbleLeftRightIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {chatSessions.length} session{chatSessions.length !== 1 ? 's' : ''} found
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Chat Sessions Table */}
            <div className="lg:col-span-2">
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-gray-400" />
                    Chat Sessions ({chatSessions.length})
                    {sessionsLoading && (
                      <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    )}
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    All chat sessions associated with this lead
                  </p>
                </div>
                <div className="px-6 py-5">
                  {chatSessions.length > 0 ? (
                    <div className="overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Session
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Started
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Messages
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Source
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {chatSessions.map((session) => (
                            <tr key={session.id} className="hover:bg-gray-50">
                              <td className="px-6 py-5 whitespace-nowrap">
                                <div className="flex flex-col">
                                  <button
                                    onClick={() => navigate(`/chat/${session.id}`)}
                                    className="text-left hover:bg-gray-50 rounded p-1 -m-1 transition-colors duration-200 group"
                                  >
                                    <div className="text-sm font-medium text-gray-900 group-hover:text-primary-600 flex items-center">
                                      {session.title || 'Untitled Session'}
                                    </div>
                                    {session.firstMessage && (
                                      <div className="text-sm text-gray-500 truncate max-w-xs group-hover:text-gray-700">
                                        {session.firstMessage.length > 60
                                          ? `${session.firstMessage.substring(0, 60)}...`
                                          : session.firstMessage}
                                      </div>
                                    )}
                                    <div className="text-xs text-gray-400 font-mono mt-1 group-hover:text-gray-500">
                                      ID: {session.id.substring(0, 8)}...
                                    </div>
                                  </button>
                                </div>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500">
                                {formatDateUpdated(session.date_created)}
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {session.messageCount || 0} message{(session.messageCount || 0) !== 1 ? 's' : ''}
                                </span>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center">
                                  {session.source_type === 'widget' ? (
                                    <>
                                      <GlobeAltIcon className="h-4 w-4 mr-1 text-green-500" />
                                      Widget
                                    </>
                                  ) : session.source_type === 'api' ? (
                                    <>
                                      <DevicePhoneMobileIcon className="h-4 w-4 mr-1 text-blue-500" />
                                      API
                                    </>
                                  ) : (
                                    <>
                                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1 text-gray-500" />
                                      Dashboard
                                    </>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-5 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => navigate(`/chat/${session.id}`)}
                                  className="btn btn-xs btn-square btn-primary-outline"
                                >
                                  <ChatBubbleLeftRightIcon className="h-3 w-3 mr-1" />
                                  Open Chat
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : sessionsLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-600">Loading sessions...</p>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                      <p>No chat sessions found for this lead.</p>
                      <p className="text-sm mt-1">This lead may have been created through a contact form or other source.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;